(function ($) {
    'use strict';

    /**
     * jQuery Plugin: glyphTextFormatter
     *
     * A plugin for formatting text selections with HTML tags and live preview.
     * Supports multiple formatting options with configurable buttons and nested formatting.
     *
     * @param {Object} options - Configuration options for the plugin
     * @param {Array} [options.formats=['bold', 'italic', 'opacity']] - Array of format types to enable
     * @param {string} [options.previewClass='glyph-text-formatter-preview'] - CSS class for preview container
     * @param {boolean} [options.showPreview=true] - Whether to show live preview
     * @param {boolean} [options.enableTooltips=true] - Whether to enable Bootstrap tooltips on buttons
     * @param {Object} [options.customFormats={}] - Custom format definitions
     *
     * @returns {jQuery} The jQuery object for chaining
     */
    $.fn.glyphTextFormatter = function (options) {
        return initializeGlyphTextFormatter(this, options);
    };

    // Default configuration
    const DEFAULT_CONFIG = {
        formats: ['bold', 'italic', 'opacity'],
        previewClass: 'glyph-text-formatter-preview',
        showPreview: true,
        customFormats: {},
        enableTooltips: true
    };

    // Built-in format definitions
    const BUILT_IN_FORMATS = {
        bold: { tag: 'b', icon: 'bi-type-bold', title: 'Bold', shortcut: 'Ctrl+B' },
        italic: { tag: 'i', icon: 'bi-type-italic', title: 'Italic', shortcut: 'Ctrl+I' },
        underline: { tag: 'u', icon: 'bi-type-underline', title: 'Underline', shortcut: 'Ctrl+U' },
        strikethrough: { tag: 's', icon: 'bi-type-strikethrough', title: 'Strikethrough', shortcut: 'Ctrl+Shift+S' },
        code: { tag: 'code', icon: 'bi-code', title: 'Code', shortcut: 'Ctrl+`' },
        mark: { tag: 'mark', icon: 'bi-highlighter', title: 'Highlight', shortcut: 'Ctrl+H' },
        newline: { tag: 'br', icon: 'bi-arrow-return-left', title: 'New line', shortcut: 'Ctrl+N' },
        opacity: { tag: 'span', icon: 'bi-eye-slash', title: 'Opacity', shortcut: 'Ctrl+O' } // No style in input
    };

    /**
     * Gets the complete format definitions by merging built-in and custom formats
     */
    const getFormatDefinitions = (config) => ({ ...BUILT_IN_FORMATS, ...config.customFormats });

    /**
     * Checks if input is already in an input-group
     */
    const isInInputGroup = ($input) => $input.parent().hasClass('input-group');

    /**
     * Creates or extends the input group structure with individual formatting buttons
     */
    const createOrExtendInputGroup = ($input, config) => {
        const formatDefinitions = getFormatDefinitions(config);
        let $inputGroup, $newInput, isExistingGroup;

        if (isInInputGroup($input)) {
            $inputGroup = $input.parent();
            $newInput = $input;
            isExistingGroup = true;
        } else {
            $inputGroup = $('<div class="input-group"></div>');
            $newInput = $input.clone(true);
            $inputGroup.append($newInput);
            $input.replaceWith($inputGroup);
            isExistingGroup = false;
        }

        // Create individual format buttons and add them to input group
        config.formats.forEach(formatType => {
            const format = formatDefinitions[formatType];
            if (format) {
                const $button = $(`
                    <button type="button" 
                            class="input-group-text glyph-formatter-btn btn-secondary" 
                            data-format="${formatType}"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title="${format.title} (${format.shortcut})">
                        <i class="bi ${format.icon}"></i>
                    </button>
                `);
                $inputGroup.append($button);
            }
        });

        // Create preview container if enabled (initially hidden)
        let $preview = null;
        if (config.showPreview) {
            $preview = $(`
                <div class="${config.previewClass} my-2 p-2 border rounded bg-light" style="display: none;">
                    <small class="text-muted d-block mb-1"><i class="bi bi-eye"></i></small>
                    <div class="preview-content"></div>
                </div>
            `);
            $inputGroup.after($preview);
        }

        return { $inputGroup, $input: $newInput, $preview, isExistingGroup };
    };

    /**
     * Wraps selected text with HTML tags
     */
    const wrapSelection = (input, format) => {
        const start = input.selectionStart;
        const end = input.selectionEnd;
        const text = input.value;
        const selectedText = text.substring(start, end);

        // Special handling for <br>
        if (format.tag === 'br') {
            const beforeText = text.substring(0, start);
            const afterText = text.substring(end);
            const insertedText = '<br>';
            input.value = beforeText + insertedText + afterText;

            // Move cursor after inserted <br>
            const newPosition = start + insertedText.length;
            input.setSelectionRange(newPosition, newPosition);
            return true;
        }

        if (selectedText.length === 0) return false;

        const beforeText = text.substring(0, start);
        const afterText = text.substring(end);

        const wrappedText = `<${format.tag}>${selectedText}</${format.tag}>`;
        input.value = beforeText + wrappedText + afterText;

        const newPosition = start + wrappedText.length;
        input.setSelectionRange(newPosition, newPosition);
        return true;
    };

    /**
     * Checks if text contains HTML formatting tags
     */
    const hasFormattingTags = (text) => /<\/?(?:b|i|u|s|code|mark|strong|em|span)\b[^>]*>/i.test(text);

    /**
     * Updates the live preview with rendered HTML and shows/hides based on content
     */
    const updatePreview = ($input, $preview) => {
        if (!$preview) return;
        const text = $input.val();
        const $previewContent = $preview.find('.preview-content');

        // Show preview if text contains formatting tags or <br>
        if (hasFormattingTags(text) || /<br\s*\/?>/i.test(text)) {
            let sanitizedHtml = text
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                // allow formatting tags
                .replace(/&lt;(\/?)(b|i|u|s|code|mark|strong|em|span)&gt;/g, '<$1$2>')
                // allow <br> tags
                .replace(/&lt;br\s*\/?&gt;/gi, '<br>');

            // Apply 50% opacity style only to <span> tags
            sanitizedHtml = sanitizedHtml.replace(/<span>/g, '<span style="opacity:0.5">');

            $previewContent.html(sanitizedHtml);
            $preview.show();
        } else {
            $preview.hide();
        }
    };

    /**
     * Creates click handler for format buttons
     */
    const createFormatHandler = (elements, config) => {
        return function(e) {
            e.preventDefault();
            e.stopPropagation();

            const $button = $(this);
            const formatType = $button.data('format');
            const formatDefinitions = getFormatDefinitions(config);
            const format = formatDefinitions[formatType];

            if (!format) return;

            const input = elements.$input[0];
            input.focus();

            const success = wrapSelection(input, format);
            if (success) {
                elements.$input.trigger('input');
                $button.addClass('btn-success').removeClass('btn-secondary');
                setTimeout(() => { $button.removeClass('btn-success').addClass('btn-secondary'); }, 200);
            } else {
                $button.addClass('btn-warning').removeClass('btn-secondary');
                setTimeout(() => { $button.removeClass('btn-warning').addClass('btn-secondary'); }, 500);
            }
        };
    };

    /**
     * Sets up keyboard shortcuts for formatting
     */
    const setupKeyboardShortcuts = (elements, config) => {
        const formatDefinitions = getFormatDefinitions(config);
        elements.$input.on('keydown.glyphTextFormatter', function(e) {
            for (const formatType of config.formats) {
                const format = formatDefinitions[formatType];
                if (!format || !format.shortcut) continue;

                const shortcut = format.shortcut.toLowerCase();
                const isCtrl = e.ctrlKey || e.metaKey;
                const isShift = e.shiftKey;
                const key = e.key.toLowerCase();

                let matches = false;
                if (shortcut.includes('ctrl+shift+')) {
                    const shortcutKey = shortcut.replace('ctrl+shift+', '');
                    matches = isCtrl && isShift && key === shortcutKey;
                } else if (shortcut.includes('ctrl+')) {
                    const shortcutKey = shortcut.replace('ctrl+', '');
                    matches = isCtrl && !isShift && key === shortcutKey;
                }

                if (matches) {
                    e.preventDefault();
                    const success = wrapSelection(this, format);
                    if (success) elements.$input.trigger('input');
                    break;
                }
            }
        });
    };

    /**
     * Main initialization function
     */
    const initializeGlyphTextFormatter = (elements, userConfig = {}) => {
        elements.each(function() {
            const $input = $(this);
            if ($input.data('glyphTextFormatter-initialized')) return;

            // Parse data attributes
            const dataFormats = $input.data('formats');
            const dataShowPreview = $input.data('show-preview');
            const dataPreviewClass = $input.data('preview-class');
            const dataEnableTooltips = $input.data('enable-tooltips');

            let dataConfig = {};
            if (dataFormats) {
                if (typeof dataFormats === 'string') {
                    try { dataConfig.formats = JSON.parse(dataFormats); }
                    catch (e) { dataConfig.formats = [dataFormats]; }
                } else if (Array.isArray(dataFormats)) { dataConfig.formats = dataFormats; }
            }
            if (dataShowPreview !== undefined) dataConfig.showPreview = dataShowPreview;
            if (dataPreviewClass) dataConfig.previewClass = dataPreviewClass;
            if (dataEnableTooltips !== undefined) dataConfig.enableTooltips = dataEnableTooltips;

            let config = { ...DEFAULT_CONFIG };
            if (dataConfig.formats) config.formats = dataConfig.formats;
            Object.keys(dataConfig).forEach(key => { if (key !== 'formats') config[key] = dataConfig[key]; });
            if (userConfig.formats) config.formats = userConfig.formats;
            Object.keys(userConfig).forEach(key => { if (key !== 'formats') config[key] = userConfig[key]; });

            const elementRefs = createOrExtendInputGroup($input, config);

            if (config.enableTooltips && typeof bootstrap !== 'undefined' && bootstrap.Tooltip) {
                elementRefs.$inputGroup.find('button[data-format]').each(function() {
                    new bootstrap.Tooltip(this);
                });
            }

            const formatHandler = createFormatHandler(elementRefs, config);
            elementRefs.$inputGroup.on('click.glyphTextFormatter', 'button.glyph-formatter-btn[data-format]', formatHandler);
            setupKeyboardShortcuts(elementRefs, config);

            if (config.showPreview && elementRefs.$preview) {
                elementRefs.$input.on('input.glyphTextFormatter keyup.glyphTextFormatter', function() {
                    updatePreview(elementRefs.$input, elementRefs.$preview);
                });
                updatePreview(elementRefs.$input, elementRefs.$preview);
            }

            $input.data('glyphTextFormatter-initialized', true);
            $input.data('glyphTextFormatter-elements', elementRefs);
        });

        return elements;
    };

    /**
     * Destroy method to clean up the plugin
     */
    $.fn.glyphTextFormatterDestroy = function() {
        return this.each(function() {
            const $input = $(this);
            const elementRefs = $input.data('glyphTextFormatter-elements');
            if (elementRefs && $input.data('glyphTextFormatter-initialized')) {
                elementRefs.$inputGroup.find('button[data-format]').each(function() {
                    const tooltipInstance = bootstrap?.Tooltip?.getInstance(this);
                    if (tooltipInstance) tooltipInstance.dispose();
                });
                elementRefs.$inputGroup.off('.glyphTextFormatter');
                $input.off('.glyphTextFormatter');
                elementRefs.$inputGroup.find('button.glyph-formatter-btn').remove();
                if (elementRefs.$preview) elementRefs.$preview.remove();
                if (!elementRefs.isExistingGroup) {
                    const $originalInput = $input.clone(true);
                    elementRefs.$inputGroup.replaceWith($originalInput);
                }
                $input.removeData('glyphTextFormatter-initialized');
                $input.removeData('glyphTextFormatter-elements');
            }
        });
    };

    // Auto-initialize on elements with glyph-text-formatter class when document is ready
    $(document).ready(function () {
        $('.glyph-text-formatter').glyphTextFormatter();
    });

})(jQuery);
